import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import {
  CardItem,
  DataGrid,
  ErrorMessage,
  FormattedPrice,
  GridColDef,
  LoadingSpinner,
  StateStore,
  useFormatting,
} from "@msys/ui";
import { Chip, Stack, TablePagination, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  OrganisationProjectsSortBy,
  OrganisationProjectsSorting,
} from "../../../clients/graphqlTypes.js";
import { RestrictedByProjectPermissionWithDebug } from "../../auth/RestrictedByProjectPermission.js";
import { useUserData } from "../../auth/useUserData.js";
import { EntityNumber } from "../../commons/EntityNumber.js";
import { CollectionView } from "../../commons/hooks/useCollectionView.js";
import { RESULTS_PER_PAGE_OPTIONS } from "../../constants.js";
import { getAddressLabel } from "../addresses/helpers.js";
import { CategoriesChips } from "../categories/CategoriesLabeledValue.js";
import { getProjectCrmLinksLabel } from "../contact-links/helper.js";
import { useRelationshipRoles } from "../contact-links/useRelationshipRoles.js";
import { useGetCustomFieldValueAsDisplayContent } from "../custom-fields/CustomFieldsBox.js";
import { useCustomFieldConfig } from "../custom-fields/useCustomFieldConfig.js";
import { ProjectStatusBadge } from "../projects/badges/ProjectStatusBadge.js";
import { TeamsCell } from "../teams/TeamsCell.js";
import { ProjectSourceCreatedBy } from "../projects/components/ProjectSourceCreatedBy.js";
import { ProjectSourceEntity } from "../projects/components/ProjectSourceEntity.js";
import { ProjectAssigneeEditButton } from "../projects/ProjectAssigneeEditButton.js";
import { ProjectPhaseChip } from "../projects/ProjectPhaseChip.js";
import { ProjectSourceFilterType } from "../projects/useProjectSources.js";
import { useProjectTypes } from "../projects/useProjectTypes.js";
import { getPersonLabel } from "../users/helpers.js";
import {
  OpportunityListItem,
  OpportunityListItemDisplayConfig,
} from "./components/OpportunityListItem.js";
import {
  OpportunitiesList_ProjectFragment,
  OpportunitiesListQueryVariables,
  useOpportunitiesListQuery,
} from "./OpportunitiesList.generated.js";
import { useQuotingStatus } from "./useQuotingStatus.js";

export type Filters = Omit<
  OpportunitiesListQueryVariables,
  "offset" | "limit" | "sorting" | "filterBySources"
> & {
  sources?: ProjectSourceFilterType[] | undefined | null;
};

export const DEFAULT_SORTING: OrganisationProjectsSorting[] = [
  {
    column: "createdAt",
    direction: "desc",
  },
];

interface Props {
  sorting: OrganisationProjectsSorting[];
  setSorting: React.Dispatch<
    React.SetStateAction<OrganisationProjectsSorting[]>
  >;
  activeView: CollectionView;
  paginationModel: { page: number; pageSize: number };
  setPaginationModel: (newPaginationModel: {
    page: number;
    pageSize: number;
  }) => void;
  showStatus?: boolean;
  showPhase?: boolean;
  stateStore: StateStore;
  displayConfig: OpportunityListItemDisplayConfig;
  variables: OpportunitiesListQueryVariables;
}

export function OpportunitiesList({
  sorting,
  setSorting,
  activeView,
  paginationModel,
  setPaginationModel,
  showStatus = true,
  showPhase = false,
  stateStore,
  displayConfig,
  variables,
}: Props) {
  const client = useApolloClient();
  const query = useOpportunitiesListQuery({
    client,
    variables,
  });
  const viewer = useUserData().currentUser!;

  const { getFieldDisplayValueForList } =
    useGetCustomFieldValueAsDisplayContent();
  const { customFieldConfig: customFieldConfigs } =
    useCustomFieldConfig("Project");

  const opportunities =
    getDataOrNull((query.data ?? query.previousData)?.projects)?.edges.map(
      e => e.node
    ) ?? [];
  const totalCount =
    getDataOrNull((query.data ?? query.previousData)?.projects)?.totalCount ??
    0;
  const phases = getDataOrNull(query.data?.organisationSettings)?.projectPhases;

  const navigate = useNavigate();
  const { t } = useTranslate([
    "Opportunities",
    "Projects",
    "Categories",
    "Global",
    "Projects",
    "ProjectMembers",
    "ProjectTypes",
  ]);

  const { getRoleLabel } = useRelationshipRoles();
  const { projectTypeLabels } = useProjectTypes();
  const { getFormattedDateTime, getFormattedDate } = useFormatting();
  const { quotingStatusLabels } = useQuotingStatus();

  const columns: GridColDef<OpportunitiesList_ProjectFragment>[] =
    React.useMemo(
      () => [
        {
          field: "number",
          headerName: t("No.", { ns: "Global" }),
          minWidth: 60,
          flex: 0.75,
          renderCell: ({ row: opportunity }) => (
            <EntityNumber noLabel number={opportunity.number} />
          ),
        },
        {
          field: "phase",
          headerName: t("Phase", { ns: "Opportunities" }),
          editable: false,
          sortable: true,
          flex: 1,
          minWidth: 100,
          renderCell: ({ row: opportunity }) => (
            <ProjectPhaseChip
              projectId={opportunity.id}
              currentState={opportunity.state}
              currentPhase={opportunity.phase}
              availablePhases={phases}
            />
          ),
        },
        {
          field: "title",
          headerName: t("Title", { ns: "Opportunities" }),
          editable: false,
          sortable: true,
          flex: 3,
          minWidth: 100,
          valueGetter: (value, opportunity) => opportunity.title,
        },
        {
          field: "organisationName",
          headerName: t("Client", { ns: "Opportunities" }),
          editable: false,
          sortable: true,
          flex: 3,
          minWidth: 100,
          valueGetter: (value, opportunity) =>
            [
              opportunity.crmOrganisation?.title,
              `${opportunity.crmOrganisation?.contactPerson?.firstname ?? ""} ${
                opportunity.crmOrganisation?.contactPerson?.familyname ?? ""
              }`.trim(),
            ]
              .filter(Boolean)
              .join(" - "),
          renderCell: ({ row: opportunity }) =>
            [
              opportunity.crmOrganisation?.title,
              `${opportunity.crmOrganisation?.contactPerson?.firstname ?? ""} ${
                opportunity.crmOrganisation?.contactPerson?.familyname ?? ""
              }`.trim(),
            ]
              .filter(Boolean)
              .join(" - "),
        },
        {
          field: "organisationEmail",
          headerName: t("Email", { ns: "Global" }),
          editable: false,
          sortable: true,
          flex: 2,
          minWidth: 100,
          valueGetter: (value, opportunity) =>
            opportunity.crmOrganisation?.email ?? "–",
        },
        {
          field: "organisationPhone",
          headerName: t("Phone", { ns: "Global" }),
          editable: false,
          sortable: false,
          flex: 2,
          minWidth: 100,
          valueGetter: (value, opportunity) =>
            opportunity.crmOrganisation?.phones?.[0]?.number ?? "–",
        },
        {
          field: "buildingStreet",
          headerName: t("Project address", { ns: "Opportunities" }),
          editable: false,
          sortable: true,
          flex: 2,
          minWidth: 100,
          valueGetter: (value, opportunity) =>
            getAddressLabel(opportunity.address),
          renderCell: ({ row: opportunity }) =>
            opportunity.address
              ? getAddressLabel(opportunity.address)
              : t("Not set", { ns: "Global" }),
        },
        {
          field: "assignee",
          headerName: t("Assignee", {
            ns: "ProjectMembers",
          }),
          editable: false,
          sortable: true,
          flex: 2,
          minWidth: 100,
          valueGetter: (value, opportunity) =>
            opportunity.assignee
              ? getPersonLabel(opportunity.assignee.user, false)
              : "–",
          renderCell: ({ row: opportunity }) => (
            <RestrictedByProjectPermissionWithDebug
              permission="project_project_update"
              project={opportunity}
              otherwise={
                <ProjectAssigneeEditButton
                  assignee={opportunity.assignee ?? null}
                  projectId={opportunity.id}
                  projectTitle={opportunity.title}
                  readOnly={true}
                  type="button"
                />
              }
            >
              <ProjectAssigneeEditButton
                assignee={opportunity.assignee ?? null}
                projectId={opportunity.id}
                projectTitle={opportunity.title}
                type="button"
              />
            </RestrictedByProjectPermissionWithDebug>
          ),
        },
        {
          field: "budget",
          headerName: t("Budget", { ns: "Opportunities" }),
          editable: false,
          sortable: true,
          flex: 1,
          minWidth: 100,
          align: "right",
          headerAlign: "right",
          valueGetter: (value, opportunity) => opportunity.budget?.amount ?? 0,
          renderCell: ({ row: opportunity }) => (
            <RestrictedByProjectPermissionWithDebug
              project={opportunity}
              permission="quote_document_read"
            >
              <Typography
                component="span"
                align="right"
                variant="body2"
                style={{ fontWeight: 500 }}
              >
                <FormattedPrice value={opportunity.budget} />
              </Typography>
            </RestrictedByProjectPermissionWithDebug>
          ),
        },
        {
          field: "source",
          headerName: t("Source", { ns: "Opportunities" }),
          editable: false,
          sortable: false,
          flex: 1,
          minWidth: 100,
          renderCell: ({ row: project }) => (
            <ProjectSourceEntity
              projectSource={project.source}
              projectId={project.id}
            />
          ),
        },
        {
          field: "state",
          headerName: t("Status", { ns: "Projects" }),
          minWidth: 120,
          sortable: true,
          flex: 1.5,
          renderCell: ({ row: project }) => (
            <ProjectStatusBadge projectState={project.state} />
          ),
        },
        {
          field: "type",
          headerName: t("Type", { ns: "ProjectTypes" }),
          minWidth: 100,
          flex: 1,
          sortable: false,
          renderCell: ({ row: project }) =>
            project.type
              ? projectTypeLabels[project.type]
              : t("Not set", { ns: "Global" }),
        },
        {
          field: "crmLinks",
          headerName: t("Other contacts", { ns: "Projects" }),
          minWidth: 100,
          sortable: false,
          flex: 1,
          renderCell: ({ row: project }) =>
            getProjectCrmLinksLabel(project, getRoleLabel),
        },
        {
          field: "category",
          headerName: t("Category", { ns: "Categories" }),
          editable: false,
          sortable: false,
          flex: 1,
          minWidth: 200,
          renderCell: ({ row: opportunity }) => (
            <CategoriesChips categoryIds={opportunity.categories} />
          ),
        },
        {
          field: "createdBy",
          headerName: t("Created by", {
            ns: "Opportunities",
          }),
          editable: false,
          sortable: true,
          flex: 1,
          minWidth: 100,
          renderCell: ({ row: project }) => (
            <ProjectSourceCreatedBy projectSource={project.source} />
          ),
        },
        {
          field: "createdAt",
          headerName: t("Created at", {
            ns: "Opportunities",
          }),
          editable: false,
          flex: 1,
          minWidth: 80,
          valueGetter: (value, opportunity) =>
            opportunity.createdAt ? new Date(opportunity.createdAt) : null,
          renderCell: ({ row: opportunity }) =>
            opportunity.createdAt
              ? getFormattedDateTime(opportunity.createdAt)
              : "-",
        },
        {
          field: "deadline",
          headerName: t("Deadline", { ns: "Projects" }),
          minWidth: 100,
          flex: 1,
          renderCell: ({ row: project }) =>
            project.deadline ? (
              <Typography
                color={project.overdue ? "error" : undefined}
                component="span"
                variant="body2"
              >
                {getFormattedDate(project.deadline)}
              </Typography>
            ) : (
              "–"
            ),
        },
        {
          field: "earliestStart",
          headerName: t("Earliest start", { ns: "Projects" }),
          minWidth: 100,
          flex: 1,
          renderCell: ({ row: project }) =>
            project.earliestStart
              ? getFormattedDate(project.earliestStart)
              : "–",
        },
        ...(viewer.organisation.capabilities.includes("quoting")
          ? [
              {
                field: "leadId",
                headerName: t("Lead ID", { ns: "Projects" }),
                flex: 1,
                hideable: true,
                sortable: false,
                valueGetter: (value, opportunity) => opportunity.leadId ?? "–",
              } as GridColDef<OpportunitiesList_ProjectFragment>,
            ]
          : []),
        ...customFieldConfigs
          .filter(e => e.showAsColumnInList)
          .map(e => {
            return {
              field: `custom:${e.key}`,
              headerName: e.label,
              minWidth: 100,
              sortable: false,
              flex: 1,
              renderCell: ({ row: project }) => {
                const field = project.customFields.find(f => f.key === e.key);
                return getFieldDisplayValueForList(
                  field
                    ? {
                        dataType: field.dataType,
                        key: field.key,
                        value: JSON.parse(field.value),
                      }
                    : undefined,
                  e
                );
              },
            } as GridColDef<OpportunitiesList_ProjectFragment>;
          }),
        ...(viewer.organisation.capabilities.includes("quoting")
          ? [
              {
                field: "quotingStatus",
                headerName: t("Quoting status", { ns: "Opportunities" }),
                editable: false,
                sortable: false,
                flex: 1,
                minWidth: 100,
                valueGetter: (value, opportunity) => opportunity.quotingStatus,
                renderCell: ({ row: opportunity }) => (
                  <Chip
                    label={quotingStatusLabels[opportunity.quotingStatus]}
                    size={"small"}
                    color={"primary"}
                    sx={{ textDecoration: "uppercase" }}
                  />
                ),
              } as GridColDef<OpportunitiesList_ProjectFragment>,
            ]
          : []),
        ...(viewer.organisation.capabilities.includes("teams")
          ? [
              {
                field: "teams",
                headerName: t("Team", { ns: "Projects" }),
                minWidth: 100,
                flex: 1,
                sortable: false,
                renderCell: ({ row: opportunity }) => (
                  <TeamsCell teams={opportunity.teams} />
                ),
              } as GridColDef<OpportunitiesList_ProjectFragment>,
            ]
          : []),
      ],
      [
        customFieldConfigs,
        getFieldDisplayValueForList,
        getFormattedDate,
        getFormattedDateTime,
        getRoleLabel,
        projectTypeLabels,
        phases,
        quotingStatusLabels,
        t,
        viewer.organisation.capabilities,
      ]
    );

  switch (activeView) {
    case "table": {
      return (
        <DataGrid
          stateStore={stateStore}
          loading={query.loading}
          hideFooter={totalCount === 0}
          columns={columns}
          rows={opportunities}
          onRowClick={({ row }) => {
            navigate(`/projects/${row.id}`);
          }}
          sortModel={sorting.map(s => ({
            field: s.column,
            sort: s.direction,
          }))}
          onSortModelChange={newModel => {
            setSorting(
              newModel.map(({ field, sort }) => ({
                column: field as OrganisationProjectsSortBy,
                direction: sort ?? "asc",
              }))
            );
          }}
          paginationModel={paginationModel}
          onPaginationModelChange={newPaginationModel => {
            setPaginationModel(newPaginationModel);
          }}
          pageSizeOptions={RESULTS_PER_PAGE_OPTIONS}
          rowCount={totalCount}
          initialState={{
            columns: {
              columnVisibilityModel: {
                number: false,
                deadline: false,
                earliestStart: false,
                type: false,
                ...(!showStatus ? { status: false } : {}),
                ...(!showPhase ? { phase: false } : {}),
              },
            },
          }}
        />
      );
    }
    case "list": {
      if (query.loading) return <LoadingSpinner />;
      if (totalCount === 0)
        return (
          <ErrorMessage
            message={t("There are no items to display", {
              ns: "Global",
            })}
          />
        );
      return (
        <Stack direction="column" spacing={1}>
          {opportunities.map(opportunity => (
            <CardItem
              key={opportunity.id}
              //@ts-ignore
              component={Link}
              to={`/projects/${opportunity.id}`}
            >
              <OpportunityListItem
                project={opportunity}
                showAssignee
                showStatus={showStatus}
                showPhase={showPhase}
                displayConfig={displayConfig}
                availablePhases={phases}
              />
            </CardItem>
          ))}
          <TablePagination
            component="div"
            count={totalCount}
            page={paginationModel.page}
            onPageChange={(event, newPage) => {
              setPaginationModel({
                pageSize: paginationModel.pageSize,
                page: newPage,
              });
            }}
            rowsPerPage={paginationModel.pageSize}
            onRowsPerPageChange={event => {
              setPaginationModel({
                pageSize: parseInt(event.target.value, 10),
                page: 0,
              });
            }}
            labelRowsPerPage={t("Per page:", { ns: "Global" })}
          />
        </Stack>
      );
    }
    default:
      return null;
  }
}
